import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import { hasActiveCommercialLicense } from "@/lib/commercialLicenseRequests";
import {
  isWithinLicenseRenewalWindow,
  LICENSE_DATA_ACCORDION_OPEN_DAYS,
  resolveLicenseExpirationDate,
} from "@/lib/licenseRenewal";
import {
  buildConfiguracionRenovacionUrl,
  LEGAL_DOCUMENTOS_PATH,
} from "@/modules/config/configuracionRoutes";
import {
  LICENSE_RENEWAL_EXPANSION_CTA_LABEL,
  TRIAL_ACTIVE_LICENSE_CTA_LABEL,
  TRIAL_ACTIVE_STATUS_MESSAGE,
  TRIAL_EXPIRED_INSTALLATION_HINT,
  TRIAL_EXPIRED_STATUS_HINT,
  resolveInstallationBarTrialState,
} from "@/modules/panelHubOnboarding";

const LICENSE_DATA_ACCORDION_VALUE = "license-data";
const GRID_CLASS =
  "grid w-full min-w-0 grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3";
const FOOTER_CLASS = "mt-2 flex w-full min-w-0 flex-wrap items-center gap-2 border-t border-slate-100 pt-2";

function CompactField({ label, title, valueClassName = "text-sm text-slate-900", children }) {
  return (
    <div className="min-w-[9rem] leading-snug" title={title || undefined}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`${valueClassName} mt-0.5 break-normal`}>{children}</div>
    </div>
  );
}

function LicenseDataAccordionShell({
  forceOpen = false,
  summaryLine = "",
  borderClassName = "border-slate-200",
  expirationAnchor = "",
  children,
}) {
  const [open, setOpen] = useState(forceOpen ? LICENSE_DATA_ACCORDION_VALUE : undefined);

  useEffect(() => {
    setOpen(forceOpen ? LICENSE_DATA_ACCORDION_VALUE : undefined);
  }, [forceOpen, expirationAnchor]);

  const collapsed = open !== LICENSE_DATA_ACCORDION_VALUE;

  return (
    <div
      className={`w-full min-w-0 h-full rounded-sm border bg-white ${borderClassName}`}
      data-testid="dashboard-installation-bar"
    >
      <Accordion type="single" collapsible value={open} onValueChange={setOpen} className="h-full">
        <AccordionItem value={LICENSE_DATA_ACCORDION_VALUE} className="border-0 h-full">
          <AccordionTrigger
            className="px-3 py-2.5 hover:no-underline [&[data-state=open]]:pb-2 h-full min-h-[3.25rem]"
            data-testid="license-data-accordion-trigger"
          >
            <span className="flex flex-col items-start gap-0.5 text-left min-w-0 pr-2">
              <span className="text-xs font-semibold text-[#0F2D5C]">Datos de Licencia</span>
              {collapsed && summaryLine ? (
                <span className="text-[10px] font-normal text-slate-500 truncate max-w-full">
                  {summaryLine}
                </span>
              ) : null}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3 pt-0">{children}</AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

/** Barra compacta: titular, RFC, plan, vigencia e ID de instalación (acordeón plegable). */
export default function InstallationLicenseBar({
  info,
  distributorWorkspace,
  canRequestLicense = false,
  showRenewalExpansionCta = false,
}) {
  const profile = info?.profile || {};
  const lic = info?.license || {};
  const payload = lic.payload || {};
  const sub = info?.subscription || {};
  const installationId = info?.installation_id || "";
  const trialDaysConfigured = Number(info?.trial_days) > 0 ? Number(info.trial_days) : 3;
  const expirationDate = resolveLicenseExpirationDate(info);
  const forceAccordionOpen = isWithinLicenseRenewalWindow(info, LICENSE_DATA_ACCORDION_OPEN_DAYS);

  if (distributorWorkspace) {
    const titularRef =
      (profile.razon_social || "").trim() ||
      (profile.nombre_comercial || "").trim() ||
      (profile.responsable || "").trim() ||
      "Distribuidor";
    const rfcRef = (profile.rfc || "").trim() || "—";
    const summaryLine = [
      "Modo distribuidor",
      "Operativa",
      installationId || null,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <LicenseDataAccordionShell
        forceOpen={false}
        summaryLine={summaryLine}
        borderClassName="border-violet-200"
        expirationAnchor="distributor"
      >
        <div className={GRID_CLASS}>
          <CompactField label="Titular / instalación" valueClassName="text-sm font-semibold text-slate-900">
            {titularRef}
          </CompactField>
          <CompactField label="RFC titular" valueClassName="font-mono text-sm text-slate-900">
            {rfcRef}
          </CompactField>
          <CompactField label="Plan / modo" valueClassName="text-sm text-slate-800">
            <BadgeCheck className="mr-0.5 inline h-3 w-3 shrink-0 text-violet-700" strokeWidth={2} />
            Modo distribuidor
          </CompactField>
          <CompactField label="Vigencia" valueClassName="text-sm text-slate-800">
            Instalación emisora — activa
          </CompactField>
          <CompactField label="Estado licencia" valueClassName="text-sm font-medium text-emerald-800">
            Operativa
          </CompactField>
          <CompactField label="ID instalación" valueClassName="font-mono text-[11px] text-slate-600">
            {installationId || "—"}
          </CompactField>
        </div>
        <p className="mt-1.5 m-0 text-[10px] text-violet-800">Referencia del distribuidor (emisión)</p>
      </LicenseDataAccordionShell>
    );
  }

  const inTrial = sub.mode === "trial";
  const titular = inTrial
    ? (profile.razon_social || "").trim() || (profile.responsable || "").trim() || "—"
    : (profile.razon_social || "").trim() ||
      (payload.licensee_name || "").trim() ||
      (profile.nombre_comercial || "").trim() ||
      (profile.responsable || "").trim() ||
      "—";

  const rfc = inTrial
    ? (profile.rfc || "").trim() || "—"
    : (profile.rfc || "").trim() || (payload.licensee_rfc || "").trim() || "—";

  let planLabel = (payload.plan || "").trim();
  if (sub.mode === "trial") {
    planLabel = `Periodo de prueba (${trialDaysConfigured} días)`;
  } else if (!planLabel) {
    planLabel = sub.mode === "licensed" ? "Licencia instalada" : "Sin licencia";
  }

  let vigencia = "—";
  let vigenciaIsoLine = null;
  if (sub.mode === "trial" && sub.trial_ends_at) {
    const te = String(sub.trial_ends_at).slice(0, 10);
    vigencia = `Prueba hasta ${formatLicenseDateForDisplay(te)}`;
    vigenciaIsoLine = `${te} · evaluación ${trialDaysConfigured} días`;
  } else if (payload.valid_from && payload.valid_to && sub.mode !== "trial") {
    const fromIso = String(payload.valid_from).slice(0, 10);
    const toIso = String(payload.valid_to).slice(0, 10);
    vigencia = `${formatLicenseDateForDisplay(fromIso)} · ${formatLicenseDateForDisplay(toIso)}`;
    vigenciaIsoLine = `${fromIso} — ${toIso}`;
  } else if (sub.trial_ends_at) {
    const te = String(sub.trial_ends_at).slice(0, 10);
    vigencia = `Prueba hasta ${formatLicenseDateForDisplay(te)}`;
    vigenciaIsoLine = te;
  }

  const statusLabels = {
    active: "Activa",
    expiring_soon: "Por vencer",
    expired: "Vencida",
    missing: "Sin clave",
    invalid: "Inválida",
    invalid_hardware: "Otro equipo",
  };
  let estado;
  const trialState = resolveInstallationBarTrialState(sub, trialDaysConfigured);
  if (sub.mode === "trial") {
    estado = trialState.estadoLabel || (sub.ok ? "Prueba activa" : "Prueba vencida");
  } else {
    estado = statusLabels[lic.status] || lic.status || (sub.ok ? "Operativa" : sub.reason || "—");
  }

  const showTrialFooter = trialState.expired;
  const showTrialActiveFooter = trialState.active && canRequestLicense;
  const hasActiveLicense = hasActiveCommercialLicense(info);
  const showRenewalExpansionFooter =
    hasActiveLicense && (canRequestLicense || showRenewalExpansionCta);

  const vigenciaTitle = [
    vigenciaIsoLine,
    trialState.active && trialState.daysLeft != null
      ? `${trialState.daysLeft} ${trialState.daysLeft === 1 ? "día restante" : "días restantes"}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const estadoClass = trialState.expired
    ? "text-sm font-medium text-rose-800"
    : "text-sm text-slate-800";

  const summaryLine = [planLabel, estado, installationId || null].filter(Boolean).join(" · ");

  return (
    <LicenseDataAccordionShell
      forceOpen={forceAccordionOpen}
      summaryLine={summaryLine}
      borderClassName="border-slate-200"
      expirationAnchor={expirationDate || ""}
    >
      <div className={GRID_CLASS}>
        <CompactField label="Titular / instalación" valueClassName="text-sm font-semibold text-slate-900">
          {titular}
        </CompactField>
        <CompactField label="RFC titular" valueClassName="font-mono text-sm text-slate-900">
          {rfc}
        </CompactField>
        <CompactField label="Plan / modo" valueClassName="text-sm capitalize text-slate-800">
          {sub.mode === "trial" ? (
            <Activity className="mr-0.5 inline h-3 w-3 shrink-0 text-amber-600" strokeWidth={2} />
          ) : (
            <BadgeCheck className="mr-0.5 inline h-3 w-3 shrink-0 text-[#3DA873]" strokeWidth={2} />
          )}
          {planLabel}
        </CompactField>
        <CompactField label="Vigencia" title={vigenciaTitle || undefined} valueClassName="text-sm text-slate-800">
          {vigencia}
        </CompactField>
        <CompactField label="Estado licencia" valueClassName={estadoClass}>
          {estado}
        </CompactField>
        <CompactField label="ID instalación" valueClassName="font-mono text-[11px] text-slate-600">
          {installationId || "—"}
        </CompactField>
      </div>

      {showTrialActiveFooter ? (
        <div className={FOOTER_CLASS} data-testid="installation-bar-trial-active-footer">
          <p className="m-0 flex-1 min-w-[12rem] text-[10px] leading-snug text-emerald-900/90">
            {TRIAL_ACTIVE_STATUS_MESSAGE}
          </p>
          <Button
            asChild
            size="sm"
            className="h-7 shrink-0 text-[11px] font-semibold bg-[#0F2D5C] hover:bg-[#0a2247] text-white"
            data-testid="installation-bar-trial-license-cta"
          >
            <Link to={buildConfiguracionRenovacionUrl()}>{TRIAL_ACTIVE_LICENSE_CTA_LABEL}</Link>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 text-[11px] text-slate-600"
            data-testid="installation-bar-trial-docs-link"
          >
            <Link to={LEGAL_DOCUMENTOS_PATH}>Ver documentos</Link>
          </Button>
        </div>
      ) : null}

      {showRenewalExpansionFooter ? (
        <div className={FOOTER_CLASS} data-testid="installation-bar-renewal-footer">
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-7 text-[11px] border-[#0F2D5C]/30 text-[#0F2D5C] hover:bg-slate-50"
            data-testid="installation-bar-renewal-cta"
          >
            <Link to={buildConfiguracionRenovacionUrl()}>{LICENSE_RENEWAL_EXPANSION_CTA_LABEL}</Link>
          </Button>
        </div>
      ) : null}

      {showTrialFooter ? (
        <div
          className={`${FOOTER_CLASS} flex-col items-start gap-1`}
          data-testid="installation-bar-trial-footer"
        >
          {trialState.expired ? (
            <p className="m-0 text-[10px] leading-snug text-rose-800/90">{TRIAL_EXPIRED_STATUS_HINT}</p>
          ) : null}
          {trialState.expired ? (
            <p className="m-0 text-[10px] leading-snug text-slate-600">{TRIAL_EXPIRED_INSTALLATION_HINT}</p>
          ) : null}
        </div>
      ) : null}
    </LicenseDataAccordionShell>
  );
}
